import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import notification from "../../../../../helpers/notification";
import TspApiService from "../../../api/tspApiService";
import useTspApiResource, { tspErrorMessage } from "../../../hooks/useTspApiResource";
import { leaderPayload, validateLeader } from "../journeyForm";
import { placeLabel, placeToRouteItem, samePlace, tidyName } from "../../../utils/tspPlace";
import { chronologicalIndex, latestVisitByLocation, placeFromRouteCity, validatePlaceDate } from "./journeyBuilderModel";

const NOTHING = () => Promise.resolve({ data: null });

// Edit Journey (/tsp/journeys/:id/edit) — the Journey Builder on an EXISTING
// journey. Places and their sections are saved as they are changed, through
// the existing APIs (route stops, city visits, book distribution, …); the
// journey is re-read after every place change, so its dates (first to last
// place date, kept by the server) and each place's visit state are always the
// server's. Only the Team Leader waits for "Update Journey".
//
// Every action returns { ok } or { ok: false, message } (the server's reason).
export default function useTspJourneyEditor(journeyId, options = {}) {
  const { autoOpenFirst = true } = options;
  const journey = useTspApiResource(() => TspApiService.getJourney(journeyId), [journeyId]);
  const visits = useTspApiResource(() => (journeyId ? TspApiService.listCityVisits({ journey_id: journeyId, limit: 100 }) : NOTHING()), [journeyId]);
  const data = journey.data;

  const [leaderDraft, setLeaderDraft] = useState(null); // null = unchanged from the journey
  const [leaderError, setLeaderError] = useState("");
  const [openCards, setOpenCards] = useState({});
  const [activeTabs, setActiveTabs] = useState({});
  const [saving, setSaving] = useState(false);
  const inFlight = useRef(false);
  const initialOpened = useRef(false);
  const pendingAddedCity = useRef(null);

  const places = useMemo(() => (data ? (data.route || []).map(placeFromRouteCity) : []), [data]);
  // The latest recorded visit at each place (one visit per place).
  const visitsByLocation = useMemo(() => latestVisitByLocation(visits.data), [visits.data]);

  useEffect(() => {
    if (!initialOpened.current && places.length > 0) {
      initialOpened.current = true;
      if (autoOpenFirst || places.length === 1) {
        const targetPlace = places[0];
        setOpenCards((current) => ({ ...current, [targetPlace.key]: true }));
        setActiveTabs((current) => ({ ...current, [targetPlace.key]: "books" }));
      }
    }
  }, [autoOpenFirst, places]);

  useEffect(() => {
    if (pendingAddedCity.current && places.length > 0) {
      const { beforeIds } = pendingAddedCity.current;
      const newlyAdded = places.find((p) => !beforeIds.has(String(p.routeCityId)));
      if (newlyAdded) {
        setOpenCards({ [newlyAdded.key]: true });
        setActiveTabs({ [newlyAdded.key]: "books" });
        pendingAddedCity.current = null;
      }
    }
  }, [places]);

  const savedLeader = data ? { name: data.leader?.name || "", userId: data.leader?.id ? String(data.leader.id) : "" } : { name: "", userId: "" };
  const leader = leaderDraft || savedLeader;

  const reloadAll = useCallback(() => {
    journey.reload();
    visits.reload();
  }, [journey, visits]);

  // One route change: call, re-read the journey, report.
  const routeChange = async (call, fallback) => {
    try {
      await call();
      journey.reload();
      return { ok: true };
    } catch (error) {
      return { ok: false, message: tspErrorMessage(error, fallback) };
    }
  };

  const addPlace = async (place, date) => {
    if (!place || !place.stateId || !place.districtId || !tidyName(place.city)) return { ok: false, message: "Select a state, a district and a taluka / town / municipality first." };
    const problem = validatePlaceDate(date);
    if (problem) return { ok: false, message: problem };
    const duplicate = places.find((existing) => samePlace(existing, { ...place, city: tidyName(place.city) }));
    if (duplicate) return { ok: false, message: `${placeLabel(duplicate)} is already in this journey.` };
    const position = chronologicalIndex(places, date) + 1;
    const beforeIds = new Set(places.map((existing) => String(existing.routeCityId)));
    pendingAddedCity.current = { beforeIds };
    const hasCoords = Number.isFinite(place.latitude) && Number.isFinite(place.longitude);
    try {
      const response = await TspApiService.addRouteCity(
        journeyId,
        {
          ...placeToRouteItem({ ...place, city: tidyName(place.city) }),
          planned_date: date,
          ...(hasCoords ? { latitude: place.latitude, longitude: place.longitude } : {}),
          ...(place.areas && place.areas.length ? { areas: place.areas } : {}),
        },
        position
      );
      const citiesList = response?.data?.cities || response?.data?.data?.cities || response?.cities || [];
      const added = citiesList.find((city) => !beforeIds.has(String(city.id)));
      if (added) {
        setOpenCards({ [`city-${added.id}`]: true });
        setActiveTabs({ [`city-${added.id}`]: "books" });
      }
      journey.reload();
      return { ok: true };
    } catch (error) {
      pendingAddedCity.current = null;
      return { ok: false, message: tspErrorMessage(error, "Could not add the place.") };
    }
  };

  const removePlace = (place) => {
    if (!window.confirm(`Remove ${placeLabel(place)} from this journey?`)) return Promise.resolve({ ok: false, message: "" });
    return routeChange(() => TspApiService.removeRouteCity(journeyId, place.routeCityId), "Could not remove the place.");
  };

  const movePlace = (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= places.length) return Promise.resolve({ ok: true });
    const ids = places.map((place) => place.routeCityId);
    [ids[index], ids[target]] = [ids[target], ids[index]];
    return routeChange(() => TspApiService.reorderRoute(journeyId, ids), "Could not move the place.");
  };

  const setPlaceDate = (place, date) => {
    const problem = validatePlaceDate(date);
    if (problem) return Promise.resolve({ ok: false, message: problem });
    return routeChange(() => TspApiService.updateRouteCity(journeyId, place.routeCityId, { planned_date: date }), "Could not change the visit date.");
  };

  const setLeader = useCallback(
    async ({ name, userId }) => {
      setLeaderDraft({ name, userId: userId ? String(userId) : "" });
      setLeaderError("");
      const problem = validateLeader({ leaderName: name, leaderUserId: userId });
      if (problem) {
        setLeaderError(problem);
        return { ok: false, message: problem };
      }
      try {
        await TspApiService.updateJourney(journeyId, leaderPayload({ leaderName: name, leaderUserId: userId }));
        notification("success", "Team Leader updated.");
        journey.reload();
        return { ok: true };
      } catch (error) {
        const message = tspErrorMessage(error, "Could not update Team Leader.");
        setLeaderError(message);
        return { ok: false, message };
      }
    },
    [journeyId, journey]
  );

  const leaderChanged = Boolean(leaderDraft) && (leaderDraft.name.trim() !== savedLeader.name || leaderDraft.userId !== savedLeader.userId);

  const saveJourney = async () => {
    return { ok: true };
  };

  return {
    journey,
    visits,
    data,
    places,
    visitsByLocation,
    leader,
    leaderError,
    leaderChanged,
    setLeader,
    saveJourney,
    saving,
    addPlace,
    removePlace,
    movePlace,
    setPlaceDate,
    reloadAll,
    openCards,
    toggleCard: (key) => setOpenCards((current) => ({ ...current, [key]: !current[key] })),
    activeTabs,
    setCardTab: (key, tab) => setActiveTabs((current) => ({ ...current, [key]: tab })),
  };
}
